import { useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import { _ } from "i18n";
import {
	createDiscoverGamesBulkSelectStore,
	DiscoverGamesBulkSelectContext,
	EMPTY_SELECTED_GAME_IDS,
	useDiscoverGamesBulkSelectStore,
	type DiscoverGamesBulkSelectContextValue,
	type DiscoverGamesBulkSelectStore,
} from "./store";

/**
 * Provides selection state for the Discover Games grid checkbox column.
 * Selection is cleared when the table page, page size, filters, or sort change.
 */
export function DiscoverGamesBulkSelectProvider({ children }: { children: ReactNode }) {
	const storeRef = useRef<DiscoverGamesBulkSelectStore | null>(null);
	if (storeRef.current === null) {
		storeRef.current = createDiscoverGamesBulkSelectStore();
	}

	return (
		<DiscoverGamesBulkSelectContext.Provider value={storeRef.current}>
			{children}
		</DiscoverGamesBulkSelectContext.Provider>
	);
}

export function useDiscoverGamesBulkSelect(): DiscoverGamesBulkSelectContextValue {
	const store = useDiscoverGamesBulkSelectStore();
	const selectedIds = useSyncExternalStore(store.subscribe, store.getSelectedIds, () => EMPTY_SELECTED_GAME_IDS);

	return useMemo(
		() => ({
			selectedIds,
			isSelected: store.isSelected,
			toggle: store.toggle,
			setMany: store.setMany,
			clear: store.clear,
		}),
		[selectedIds, store],
	);
}

export function DiscoverGamesBulkSelectAllHeader({ pageRowIds }: { pageRowIds: number[] }) {
	const { selectedIds, setMany } = useDiscoverGamesBulkSelect();
	const allSelected = pageRowIds.length > 0 && pageRowIds.every((id) => selectedIds.has(id));

	return (
		<div className="discoverGamesTableV3-bulkSelectHeader" onClick={(e) => e.stopPropagation()}>
			<input
				type="checkbox"
				checked={allSelected}
				onChange={() => setMany(pageRowIds, !allSelected)}
				aria-label={_("discover.gamesTable.aria.bulkSelectAll")}
			/>
		</div>
	);
}
